import { Field, FieldProps, getIn } from 'formik';
import { ReactNode } from 'react';

import { CountrySelector, ParsedCountry, defaultCountries, parseCountry, CountryIso2 } from 'react-international-phone';
import 'react-international-phone/style.css';

import { Col, Input, Row } from 'antd';
import { cx } from 'common/utils/cx';
import { Control } from '../../components/Form/Control';

interface Props {
  name: string;
  label?: ReactNode;
  placeholder?: string;
  enterButton?: string | ReactNode;
  onSearch?: (phone: { number: string; prefix: string }) => void;
}

function resolvePhoneFieldError(error: unknown): string | undefined {
  if (!error) {
    return undefined;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error === 'object') {
    const phoneError = error as { number?: unknown; prefix?: unknown };
    if (typeof phoneError.number === 'string') {
      return phoneError.number;
    }
    if (typeof phoneError.prefix === 'string') {
      return phoneError.prefix;
    }
  }
  return undefined;
}

function dialCodeToIso2(dialCode: string): CountryIso2 {
  const normalized = dialCode.replace('+', '');
  for (const country of defaultCountries) {
    const parsed = parseCountry(country);
    if (parsed.dialCode === normalized) {
      return parsed.iso2;
    }
  }
  return 'cz';
}

export default function PhoneInputField({ name, label, placeholder, enterButton, onSearch, ...rest }: Props) {
  return (
    <Field name={name}>
      {({ field, form: { errors, setFieldValue } }: FieldProps) => {
        const fieldError = getIn(errors, name);
        const errorMessage = resolvePhoneFieldError(fieldError);
        const hasError = Boolean(errorMessage);
        const selectedCountry = dialCodeToIso2(field.value.prefix);

        return (
          <Control label={label} error={errorMessage}>
            <Row>
              <Col xs={enterButton ? 24 : 12} md={7}>
                <div
                  className={cx('phone-input-prefix', { 'ant-input-warning': hasError })}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '41px',
                    borderRadius: '4px',
                    padding: '0 8px',
                  }}
                >
                  <CountrySelector
                    selectedCountry={selectedCountry}
                    onSelect={(country: ParsedCountry) => setFieldValue(`${name}.prefix`, country.dialCode)}
                    preferredCountries={['cz', 'sk', 'at']}
                  />
                  <span style={{ marginLeft: '4px', whiteSpace: 'nowrap' }}>+{field.value.prefix}</span>
                </div>
              </Col>
              {enterButton ? (
                <Col xs={24} md={17}>
                  <Input.Search
                    type="text"
                    name={`${name}.number`}
                    onChange={field.onChange}
                    placeholder={placeholder}
                    value={field.value.number}
                    className={cx({ 'ant-input-warning': hasError })}
                    enterButton={enterButton}
                    onSearch={() =>
                      onSearch?.({
                        prefix: field.value.prefix,
                        number: field.value.number,
                      })
                    }
                    {...rest}
                  />
                </Col>
              ) : (
                <Col xs={12} md={17}>
                  <Input
                    type="text"
                    name={`${name}.number`}
                    onChange={field.onChange}
                    placeholder={placeholder}
                    value={field.value.number}
                    className={cx({ 'ant-input-warning': hasError })}
                    {...rest}
                  />
                </Col>
              )}
            </Row>
          </Control>
        );
      }}
    </Field>
  );
}
